import * as React from 'react';
import { getDynamicData, type DynamicDataContext, type DynamicDataObservable } from './';

export type UseDynamicDataOptions<TData, TSelected> = {
  shouldResolveData?: boolean;
  shouldUpdate?(prevSelected: TSelected, nextSelected: TSelected): boolean;
  selector?(data: TData | undefined): TSelected;
  context?: DynamicDataContext;
};

function defaultShouldReRender() {
  return true;
}

function defaultSelector<TData, TSelected>(data: TData): TSelected {
  return data as any;
}

const UNDEFINED = {};

export type DynamicDataTuple<TData, TSelected = TData | undefined> = [
  data: TSelected,
  observable: DynamicDataObservable<TData>,
];

export function useDynamicDataIsLoading<TData extends Excite.ContentApi.IDynamicData>(
  context?: DynamicDataContext,
  type?: string,
) {
  const dynamicData = getDynamicData<TData>(context, type);

  if (!__BROWSER__) {
    return dynamicData.currentSync() === undefined;
  }

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [, forceUpdate] = React.useReducer(x => x + 1, 0);

  // We use a ref instead of state because we get different dynamicData
  // instances when the context is changed
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const ref = React.useRef({
    value: dynamicData.currentSync() === undefined,
  });

  // eslint-disable-next-line react-hooks/rules-of-hooks
  React.useEffect(() => {
    const unsubscribeLoading = dynamicData.loading.on(value => {
      if (ref.current.value !== value) {
        forceUpdate();
      }
    });

    return unsubscribeLoading;
  }, [dynamicData]);

  if (dynamicData.currentSync() === undefined) {
    ref.current.value = true;
  } else {
    ref.current.value = false;
  }

  return ref.current.value;
}

export function useDynamicData<TData extends Excite.ContentApi.IDynamicData, TSelected = TData | undefined>(
  options: UseDynamicDataOptions<TData, TSelected> = {},
  type?: string,
): DynamicDataTuple<TData, TSelected> {
  const { shouldResolveData, shouldUpdate = defaultShouldReRender, selector = defaultSelector } = options;
  const dynamicData = getDynamicData<TData>(options?.context, type);

  if (!__BROWSER__) {
    return [selector(dynamicData.currentSync()), dynamicData] as DynamicDataTuple<TData, TSelected>;
  }

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const refs = React.useRef({
    // We use this ref to compare new values with previous returned values
    // because we store the selector in a ref (to keep the listener intact
    // without using `useMemo()` everywhere) and can therefor change without
    // us knowing
    value: UNDEFINED as TSelected,
    shouldUpdate,
    selector,
  });

  refs.current.selector = selector;
  refs.current.shouldUpdate = shouldUpdate;

  if (refs.current.value === UNDEFINED) {
    // Only set real value on initial render
    refs.current.value = selector(dynamicData.currentSync());
  }

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [, forceUpdate] = React.useReducer(x => x + 1, 0);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  React.useEffect(() => {
    if (shouldResolveData !== false) {
      dynamicData.current();
    }
  }, [dynamicData, shouldResolveData]);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  React.useEffect(() => {
    const unsubscribe = dynamicData.on(nextData => {
      const { value, shouldUpdate, selector } = refs.current;
      const nextValue = selector(nextData);
      if (value !== nextValue && shouldUpdate(value, nextValue)) {
        refs.current.value = nextValue;
        forceUpdate();
      }
    }, options?.context !== undefined);

    return unsubscribe;
  }, [dynamicData, options?.context]);

  return [refs.current.value, dynamicData];
}
